import { CustomStyleProperty, CustomStyleRule, Palette } from 'Shared/model/venueSettings';
import { ClassContext, RuleProperty, nestedSelector, ruleProperties, settingsParts }
	from 'Shared/frontend/styles/ruleProperties';
import { createCssFromSettings } from 'Shared/frontend/widgets/createCssFromSettings';


/*
	One property's whole css contribution, flat - the 'css' field spread in, and margin / padding /
	border folded to their long-hand keys (createCssFromSettings does that for the page resolver; a
	rule resolved by selector has to do it here so the keys land inside the nested selector, not at
	the top).
*/
export function resolvePropertyCss(property:RuleProperty, params:CustomStyleProperty['params']): object
{
	const css: any = {};
	const settings: any = {};

	for (const [field,fn] of Object.entries(property.fields)) {
		const value = fn(params, false);
		if (value === undefined)
			continue;
		if (field == 'css')
			Object.assign(css, value);
		else
			createCssFromSettings(field, value, css, settings);
	}

	return css;
}


/*
	The whole css a settings-page rule list contributes, as one object: the parts that dress the
	element itself sit flat at the top, and everything else under the selector its part points at.

	One object rather than one rule per property, because the page resolver shallow-merges the css of
	the rules that apply (see applyRule) - two properties handed over separately under the same
	selector key would clobber each other rather than merging.

	A rule that names no part at all is a rule the venue has just added and not yet pointed anywhere -
	the shape defaultRule() creates - and contributes nothing, quietly. One that names a part this
	context has no such thing as is a different matter: a document written by hand, or a part since
	withdrawn. Letting that through would dress the whole node, since an unknown part resolves to no
	selector, so it is dropped and said so in the log.
*/
export function settingRulesCss(rules:CustomStyleRule[], context:ClassContext,
	palette:Palette): object
{
	const properties = ruleProperties(palette);
	const css: any = {};

	for (const rule of rules) {
		if (rule.location != 'class')
			continue;

		const part = rule.locationClass ?? '';
		if (!(settingsParts[context] ?? []).some(entry => entry.part==part)) {
			log.warn(`Settings rule names no part of ${context}: ${part}`);
			continue;
		}

		const selector = nestedSelector(context,part);
		if (selector != '' && css[selector] == undefined)
			css[selector] = {};
		const target = selector=='' ? css : css[selector];

		for (const entry of rule.properties) {
			const property = properties[entry.property];
			if (property == undefined) {
				log.warn(`Settings rule property not found: ${context} / ${entry.property}`);
				continue;
			}
			Object.assign(target, resolvePropertyCss(property,entry.params));
		}
	}

	return css;
}
